/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {Router} from '@angular/router';
import {isPlatformBrowser} from '@angular/common';
import {environment} from '@environments/environment';

/** Trang hội thoại V2. Không còn hỗ trợ quay lại V1 (/app/conversation chỉ còn là link cũ, guard chuyển về đây). */
const CONVERSATION_PATH = '/conversation';

/** Chỉ viewport điện thoại/tablet dọc nhỏ dùng UI app riêng. */
const APP_LAYOUT_MAX_WIDTH = 992;

export interface IDeviceLayoutInfo {
    width: number;
    /** Thiết bị nhập chính là cảm ứng (matchMedia pointer: coarse) */
    coarsePointer: boolean;
    userAgent: string;
    /** Đang chạy trong app mobile (app native ghi cờ isMobileApp vào localStorage) */
    isMobileApp: boolean;
}

/**
 * Thiết bị nên dùng shell app thay bản web V2: đang trong app mobile, hoặc màn < 992px trên thiết bị cầm tay
 * (cảm ứng / User-Agent điện thoại). Màn nhỏ trên máy tính (thu nhỏ cửa sổ) vẫn giữ bản web.
 */
export function shouldUseAppLayout(device: IDeviceLayoutInfo): boolean {
    if (device.isMobileApp) {
        return true;
    }
    const isHandheld = device.coarsePointer || /Mobi|iPhone|iPod|Android/i.test(device.userAgent);
    return device.width < APP_LAYOUT_MAX_WIDTH && isHandheld;
}

@Injectable({providedIn: 'root'})
export class LayoutUIService {
    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private router: Router,
    ) {
    }

    /**
     * BE mặc định trả bản web V2 khi chưa có cookie layoutUI (không biết màn hình). Bản web chạy trên điện thoại /
     * app mobile → chuyển trang sang cùng URL kèm layoutUI=app để PHP trả shell app và ghi nhớ cookie.
     * Trả về true nếu đang chuyển trang (app không cần khởi tạo tiếp).
     */
    ensureDeviceLayout(): boolean {
        if (!isPlatformBrowser(this.platformId) || environment.isMobileLayout || environment.isAdsModule) {
            return false;
        }

        const url = new URL(window.location.href);
        // Đã yêu cầu app mà PHP vẫn trả bản web (VD: môi trường chưa có build app) → không chuyển lặp
        if (url.searchParams.get('layoutUI') === 'app') {
            return false;
        }

        let isMobileApp = false;
        try {
            isMobileApp = !!localStorage.getItem('isMobileApp');
        } catch { /* Trình duyệt chặn storage → coi như không phải app mobile. */ }

        const useAppLayout = shouldUseAppLayout({
            width: window.innerWidth,
            coarsePointer: !!window.matchMedia?.('(pointer: coarse)')?.matches,
            userAgent: navigator.userAgent,
            isMobileApp,
        });
        if (!useAppLayout) {
            return false;
        }

        url.searchParams.set('layoutUI', 'app');
        window.location.replace(url.toString());
        return true;
    }

    /**
     * URL trang hội thoại V2: giữ query params hiện tại (ví dụ cid) và ghi đè layoutUI.
     * Viewport mobile phải đi qua shell `app`, không dùng bản web V2
     * responsive để PHP trả đúng layout ngay từ request đầu tiên.
     */
    getConversationUrl(): string {
        const targetUrl = this.router.parseUrl(CONVERSATION_PATH);
        targetUrl.queryParams = {
            ...this.router.parseUrl(this.router.url).queryParams,
            layoutUI: this.getLayoutUiRequestValue(),
        };
        return this.router.serializeUrl(targetUrl);
    }

    private getLayoutUiRequestValue(): 'v2' | 'app' {
        return isPlatformBrowser(this.platformId) && window.innerWidth < APP_LAYOUT_MAX_WIDTH ? 'app' : 'v2';
    }
}
